import { FC } from 'react';
import { createUseStyles } from 'react-jss';
import { IonText } from '@ionic/react';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { IPatient } from '../../caremap/ICareMap';
import { DrawerSectionHeading } from '../../components/DrawerSectionHeading';
import { CriticalInfoItemView } from './CriticalInfoItemView';
import { CriticalInfoPresenter } from './CriticalInfoPresenter';

const useStyles = createUseStyles({
    // a single bounded surface, unlike the navigable lists around it. This section is reference
    // data nobody edits from here, and the frame is what says so at a glance
    card: {
        border: '1px solid var(--ion-color-step-150)',
        borderRadius: 14,
        overflow: 'hidden'
    },

    // one fact per band, divided by a hairline. Labels sit above their values rather than in a
    // fixed rail so a carrier name or a licence number gets the whole card width and never has to
    // wrap into a gutter
    row: {
        padding: '12px 15px',
        borderTop: '1px solid var(--ion-color-step-100)',

        '&:first-child': {
            borderTop: 'none'
        }
    },

    label: {
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: '0.07em',
        textTransform: 'uppercase',
        lineHeight: 1.35,
        display: 'block'
    },

    // wider than the 5px between an item and its own details, so a group holding several entries
    // (surgical history) reads as separate facts rather than one long one
    body: {
        marginTop: 5,
        display: 'flex',
        flexDirection: 'column',
        gap: 13
    }
});

export interface ICriticalInfoSectionProps {
    patient: IPatient;
};

/**
 * Facts a case manager needs before they can act: code status, coverage, identification. Unlike the
 * medication and allergy sections this renders nothing at all when there is nothing recorded, so a
 * care map that has never set any of it looks exactly as it did before the section existed.
 */
export const CriticalInfoSection: FC<ICriticalInfoSectionProps> = ({ patient }) => {
    const classes = useStyles();
    const presenter = useInstance(CriticalInfoPresenter);

    const groups = presenter.getGroups(patient.criticalInfo);

    if (groups.length === 0) {
        return null;
    }

    return (
        <div data-type="critical-info">
            <DrawerSectionHeading text="Critical Info" />

            <div className={classes.card}>
                {groups.map(group => (
                    <div key={group.label} className={classes.row} data-type="critical-info-group" data-group-label={group.label}>
                        <IonText color="medium" className={classes.label}>{group.label}</IonText>

                        <div className={classes.body}>
                            {group.items.map((item, idx) => (
                                <CriticalInfoItemView key={`${group.label}-${idx}`} item={item} />
                            ))}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};
